import { motion } from 'framer-motion';
import React from 'react';

import styles from './TextCloud.module.css';

/**
 * TextCloud Props
 */
type TextCloudProps = {
  readonly content: {
    readonly text: string;
    readonly title?: string;
  };
  readonly position?: 'top' | 'bottom';
  readonly fixed?: boolean;
};

/**
 * TextCloud Component
 *
 * Displays text content in cloud-shaped containers positioned around
 * the circular menu. Features:
 * - Arc/rainbow cloud shape
 * - Scrollable overflow for long text
 * - Pink/orange theme colors
 * - Positioned above or below menu
 * - Smooth entrance animation
 * @param root0
 * @param root0.content
 * @param root0.position
 */
const TextCloud: React.FC<TextCloudProps> = ({ content, position = 'top', fixed = false }) => {
  const { text, title } = content;

  // Animation variants for entrance
  const variants = {
    hidden: {
      opacity: 0,
      y: position === 'top' ? -50 : 50,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
  };

  const transition = {
    duration: 0.6,
    ease: 'easeOut' as const,
  };

  const classNames = `${styles.textCloud} ${styles[position]} ${fixed ? styles.fixed : ''}`;

  return (
    <motion.div
      className={classNames}
      variants={variants}
      initial="hidden"
      animate="visible"
      transition={transition}
    >
      <div className={styles.cloudInner}>
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        <div className={styles.textContent}>
          <p>{text}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default TextCloud;
